import { money } from "../api.js";
import {
  explainMoney,
  jobLabel,
  kpi,
  ladderLabel,
  moneyRange,
  pct,
  ticketMixHtml,
  tip,
} from "../ux.js";

export function renderLadder(pack) {
  const ladder = pack.sales_ladder || {};
  const brands = ladder.brands || {};
  const jobs = ladder.jobs || {};
  const roll = pack.product_rollup || {};

  const brandCards = Object.entries(brands)
    .map(
      ([id, b]) => `
      <div class="panel brand-card">
        <strong>${b.name || id}</strong>
        <div class="muted">${b.tagline || ""}</div>
        <div class="muted" style="margin-top:.4rem">
          ${b.url ? `<a href="${b.url}" target="_blank" rel="noopener">Website ↗</a>` : id}
        </div>
      </div>`
    )
    .join("");

  const jobCards = Object.entries(jobs)
    .map(([jobId, cfg]) => {
      const rungs = ["good", "better", "best"]
        .map((r) => {
          const row = cfg[r];
          if (!row) return "";
          const lab = ladderLabel(r);
          return `<div class="rung ${r}">
            <strong>${lab.full}</strong>
            <div class="muted">${row.brand || ""} — ${row.why || lab.tip}</div>
          </div>`;
        })
        .join("");
      return `<div class="panel">
        <h2>${cfg.label || jobLabel(jobId)}</h2>
        ${rungs}
      </div>`;
    })
    .join("");

  const jobRev = Object.entries(roll.job_revenue || {})
    .sort((a, b) => (b[1].expected || 0) - (a[1].expected || 0))
    .map(([job, j]) => {
      const avg = j.count ? Math.round((j.package_mid || 0) / j.count) : 0;
      return `<tr>
        <td><strong>${jobLabel(job)}</strong></td>
        <td>${j.count || 0}</td>
        <td>${money(avg)}</td>
        <td>${moneyRange(j.rev_low, j.rev_high)}</td>
        <td><strong>${money(j.expected)}</strong></td>
      </tr>`;
    })
    .join("");

  const topMk = (roll.top_markets_by_expected || [])
    .slice(0, 8)
    .map(
      (m) => `<tr>
        <td><strong>${m.name || m.market_id}</strong></td>
        <td>${m.leads}</td>
        <td>${moneyRange(m.pkg_low, m.pkg_high)}</td>
        <td><strong>${money(m.expected)}</strong></td>
      </tr>`
    )
    .join("");

  const ladderMix = Object.entries(roll.ladder_counts || {})
    .map(([k, v]) => {
      const lab = ladderLabel(k);
      return `${lab.short}: <strong>${v}</strong>`;
    })
    .join(" · ");

  return `
    <h1>Revenue &amp; products</h1>
    <p class="sub">What to sell, typical tickets, and planning revenue for this pack</p>

    ${tip(
      "In plain English",
      "We suggest a <strong>product mix</strong> (brand + good/better/best) for each home, " +
        "then estimate an installed price range. <strong>Likely revenue</strong> multiplies that by a rough chance of closing. " +
        "Use this to pick markets and prioritize — always site-measure before quoting."
    )}

    <div class="kpis">
      ${kpi(money(roll.avg_package_mid), "Typical ticket", "Average middle package price")}
      ${kpi(pct(roll.avg_close_probability), "Avg. chance of close", "Planning %")}
      ${kpi(money(roll.expected_pipeline_rev), "Likely revenue", "Best single planning number")}
      ${kpi(money(roll.expected_pipeline_conservative), "Safer case", "70% of likely")}
      ${kpi(money(roll.expected_pipeline_stretch), "Stretch case", "Optimistic")}
      ${kpi(
        moneyRange(roll.sum_package_rev_low, roll.sum_package_rev_high),
        "If every lead bought",
        "Sum of all package ranges"
      )}
    </div>

    ${explainMoney()}

    <h2>How big are the jobs?</h2>
    <div class="panel">
      ${ticketMixHtml(roll.ticket_buckets)}
      ${ladderMix ? `<p class="muted" style="margin-top:.75rem">Opening recommendation mix: ${ladderMix}</p>` : ""}
      ${
        roll.dealer_floor_leads
          ? `<p class="muted">Dealer minimum ticket lifted <strong>${roll.dealer_floor_leads}</strong> suggestions into your target band.</p>`
          : ""
      }
    </div>

    <h2>Money by job type</h2>
    <div class="table-wrap">
      <table class="data">
        <thead>
          <tr>
            <th>What kind of job</th>
            <th>Leads</th>
            <th>Avg ticket</th>
            <th>Package range</th>
            <th>Likely revenue</th>
          </tr>
        </thead>
        <tbody>${jobRev || `<tr><td colspan="5" class="muted">No revenue breakdown yet</td></tr>`}</tbody>
      </table>
    </div>

    ${
      topMk
        ? `
    <h2>Top markets (by likely revenue)</h2>
    <div class="table-wrap">
      <table class="data">
        <thead>
          <tr>
            <th>Market</th>
            <th>Leads</th>
            <th>Package range</th>
            <th>Likely revenue</th>
          </tr>
        </thead>
        <tbody>${topMk}</tbody>
      </table>
    </div>`
        : ""
    }

    <h2>Brands you can recommend</h2>
    <div class="ladder-grid">${brandCards || `<p class="muted">No brands in pack</p>`}</div>

    <h2>Good · Better · Best by situation</h2>
    <p class="muted">When you arrive on site, open with “Better” unless the home clearly needs entry or estate pricing.</p>
    <div class="ladder-grid">${jobCards}</div>

    <h2>Products suggested in this pack</h2>
    <div class="panel muted sku-chips">
      ${
        Object.entries(roll.sku_unit_counts || {})
          .map(
            ([k, v]) =>
              `<span class="chip">${k.replace(/_/g, " ")} <strong>×${v}</strong></span>`
          )
          .join(" ") || "—"
      }
    </div>

    <p class="muted small disclaimer-line">${roll.disclaimer || ""}</p>
    <div class="toolbar" style="margin-top:1rem">
      <a class="btn primary" href="#/pack">See leads with packages →</a>
      <a class="btn" href="#/">Back to overview</a>
    </div>
  `;
}
